import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Select from 'react-select';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ConfirmModal from './components/ConfirmModal';

// Helper functions for time formatting
const formatDecimalHoursToHHMM = (decimalHours) => {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

const parseHHMMToDecimalHours = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours + (minutes / 60);
};

// Parse flexible time input - supports both decimal (0,1) and HH:MM (1:30) formats
const parseFlexibleTimeToDecimalHours = (timeString) => {
  if (!timeString || !timeString.trim()) return 0;
  
  const trimmed = timeString.trim();
  
  // Check if it's decimal format (contains comma)
  if (trimmed.includes(',')) {
    const decimalValue = parseFloat(trimmed.replace(',', '.'));
    if (isNaN(decimalValue)) throw new Error('Invalid decimal format');
    return decimalValue;
  }
  
  // Check if it's HH:MM format (contains colon)
  if (trimmed.includes(':')) {
    return parseHHMMToDecimalHours(trimmed);
  }
  
  // If it's just a number, treat as decimal hours
  const decimalValue = parseFloat(trimmed);
  if (isNaN(decimalValue)) throw new Error('Invalid number format');
  return decimalValue;
};

// Extract abbreviation from worktype name (e.g., "(IT) Diverse IT" -> "(IT)")
const extractAbbreviation = (workTypeName) => {
  const match = workTypeName.match(/^\(([^)]+)\)/);
  return match ? `(${match[1]})` : workTypeName;
};

// Ferieafrunding - afrund til nærmeste halve eller hele arbejdsdag
const applyVacationRounding = (hours, timecodeName) => {
  // Check if timecode contains "FER" (case insensitive)
  if (timecodeName && timecodeName.toUpperCase().includes('FER')) {
    // Round to either 3.75 (half day) or 7.5 (full day)
    // Midpoint at 5.625 hours = (3.75 + 7.5) / 2
    return hours <= 5.625 ? 3.75 : 7.5;
  }
  return hours; // No rounding for non-vacation codes
};

export default function EventFormModal({ isOpen, onClose, onSave, start, end, projects, initialData, onDelete, selectedSlotDate, currentView, modalPosition, onTimeChange, setModalPosition, getModalPositionStyle }) {
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [hours, setHours] = useState('');
  const [projectId, setProjectId] = useState('');
  const [timecodeId, setTimecodeId] = useState('');
  const [title, setTitle] = useState('');
  const [billingtype, setBillingType] = useState('');
  const [projectTimecodes, setProjectTimecodes] = useState([]);
  const [endTime, setEndTime] = useState('');
  const [lastChangedField, setLastChangedField] = useState(null);
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [sendToCapLegal, setSendToCapLegal] = useState(true);
  const [vacationRoundingMessage, setVacationRoundingMessage] = useState('');
  // State for tracking screen size for responsive modal positioning
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const readonly = initialData?.approval_status === 'Approved';

  const canEditProject = !readonly && (initialData?.projectId === '274231000012917617');


  const resetForm = () => {
    setDate('');
    setStartTime('');
    setHours('');
    setProjectId('');
    setTimecodeId('');
    setTitle('');
    setBillingType('');
    setProjectTimecodes([]);
    setEndTime('');
    setSendToCapLegal(true);
    setVacationRoundingMessage('');
  };

  useEffect(() => {
    if (hasSubmitted && Object.keys(errors).length === 0) {
      setHasSubmitted(false);
    }
  }, [errors, hasSubmitted]);

  // Listen for window resize to update mobile state for responsive modal positioning
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update startTime/endTime -> hours
    useEffect(() => {
      if (lastChangedField === 'startTime' || lastChangedField === 'endTime') {
        if (startTime && endTime && date) {
          const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
          const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
          if (endMoment.isAfter(startMoment)) {
            const diffInHours = endMoment.diff(startMoment, 'minutes') / 60;
            setHours(formatDecimalHoursToHHMM(diffInHours));

            // Notify parent to update event in calendar
            if (onTimeChange) {
              onTimeChange({
                ...(initialData || {}),
                start: startMoment.toDate(),
                end: endMoment.toDate(),
                logId: initialData?.logId || null,
              });
            }
          }
        }
      }
    }, [startTime, endTime, date]); 

  useEffect(() => {
    if (initialData) {
      setDate(moment(initialData.start).format('YYYY-MM-DD'));
      setStartTime(moment(initialData.start).format('HH:mm'));
      const durationMs = moment(initialData.end).diff(moment(initialData.start));
      const durationHours = durationMs / 3600000; // ms to hours
      setHours(formatDecimalHoursToHHMM(durationHours));
      setProjectId(initialData.projectId || '');
      setTimecodeId(initialData.timecodeId || '');
      setTitle(initialData.title || '');
      setBillingType(initialData.billingtype || '');
      setEndTime(moment(initialData.end).format('HH:mm'));
    } else if (start && end) {
      setDate(moment(start).format('YYYY-MM-DD'));
      setStartTime(moment(start).format('HH:mm'));
      setEndTime(moment(end).format('HH:mm'));
      const durationMs = moment(end).diff(moment(start));
      const durationHours = durationMs / 3600000; // ms to hours
      setHours(formatDecimalHoursToHHMM(durationHours));
    }
  }, [start, end, initialData]);
  
  useEffect(() => {
    if (!isOpen) return;

    if (readonly) return;

    const handleArrowKeys = (event) => {
      const tag = document.activeElement.tagName;
      const isEditable = (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        document.activeElement.isContentEditable
      );
      if (isEditable) return;

      if (!readonly && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        event.preventDefault();

        const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
        const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
        if (!startMoment.isValid() || !endMoment.isValid()) return;

        const diff = endMoment.diff(startMoment, 'minutes');
        if (diff <= 0) return;

        const delta = event.key === 'ArrowUp' ? -15 : 15;

        if (event.shiftKey) {
          // Shift + Arrow: move only end time
          const newEnd = endMoment.clone().add(delta, 'minutes');
          // Check if new duration would exceed 23 hours
          const newDurationHours = newEnd.diff(startMoment, 'minutes') / 60;
          // Prevent end before start, after day end, or duration over 23 hours
          if (
            newEnd.isSameOrBefore(startMoment) ||
            newEnd.isAfter(moment(date).endOf('day').add(1, 'minute')) ||
            newDurationHours > 23
          ) {
            return;
          }
          setEndTime(newEnd.format('HH:mm'));
          setLastChangedField('endTime');

          // Notify parent
          if (onTimeChange && initialData) {
            onTimeChange({
              ...initialData,
              start: startMoment.toDate(),
              end: newEnd.toDate(),
            });
          }
          if (onTimeChange && !initialData && start && end) {
            onTimeChange({
              start: startMoment.toDate(),
              end: newEnd.toDate(),
              logId: null,
            });
          }
        } else {
          // Arrow: move both start and end
          const newStart = startMoment.clone().add(delta, 'minutes');
          const newEnd = endMoment.clone().add(delta, 'minutes');
          // Check if duration would exceed 23 hours (should remain same, but double-check)
          const durationHours = newEnd.diff(newStart, 'minutes') / 60;
          if (
            newStart.isBefore(moment(date).startOf('day')) ||
            newEnd.isAfter(moment(date).endOf('day').add(1, 'minute')) ||
            durationHours > 23
          ) {
            return;
          }
          setStartTime(newStart.format('HH:mm'));
          setEndTime(newEnd.format('HH:mm'));
          setLastChangedField('startTime');

          // Notify parent
          if (onTimeChange && initialData) {
            onTimeChange({
              ...initialData,
              start: newStart.toDate(),
              end: newEnd.toDate(),
            });
          }
          if (onTimeChange && !initialData && start && end) {
            onTimeChange({
              start: newStart.toDate(),
              end: newEnd.toDate(),
              logId: null,
            });
          }
        }
      }
      if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && (currentView === 'week' || currentView === 'work_week')) {
        event.preventDefault();

        const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
        const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
        if (!startMoment.isValid() || !endMoment.isValid()) return;

        const dayOfWeek = startMoment.day(); // 0=Sunday, 1=Monday, ..., 6=Saturday

        // Week view: block left on Monday, right on Sunday
        if (currentView === 'week') {
          if (event.key === 'ArrowLeft' && dayOfWeek === 1) return;
          if (event.key === 'ArrowRight' && dayOfWeek === 0) return;
        }
        // Work week: block left on Monday, right on Friday
        if (currentView === 'work_week') {
          if (event.key === 'ArrowLeft' && dayOfWeek === 1) return;
          if (event.key === 'ArrowRight' && dayOfWeek === 5) return;
          // Also block moving to weekend
          if (event.key === 'ArrowRight' && dayOfWeek >= 5) return;
          if (event.key === 'ArrowLeft' && dayOfWeek <= 1) return;
        }

        // Calculate new start/end
        const deltaDays = event.key === 'ArrowLeft' ? -1 : 1;
        const newStart = startMoment.clone().add(deltaDays, 'days');
        const newEnd = endMoment.clone().add(deltaDays, 'days');

        // For work_week, prevent moving to Sat/Sun
        if (currentView === 'work_week') {
          const newDay = newStart.day();
          if (newDay === 0 || newDay === 6) return;
        }

        setDate(newStart.format('YYYY-MM-DD'));
        setStartTime(newStart.format('HH:mm'));
        setEndTime(newEnd.format('HH:mm'));
        setLastChangedField('startTime');

        // Update modal position so it doesn't cover the event
        setModalPosition(getModalPositionStyle(newStart.toDate(), currentView));

        // Notify parent
        if (onTimeChange && initialData) {
          onTimeChange({
            ...initialData,
            start: newStart.toDate(),
            end: newEnd.toDate(),
          });
        }
        if (onTimeChange && !initialData && start && end) {
          onTimeChange({
            start: newStart.toDate(),
            end: newEnd.toDate(),
            logId: null,
          });
        }
      }
    };

    document.addEventListener('keydown', handleArrowKeys);
    return () => document.removeEventListener('keydown', handleArrowKeys);
  }, [isOpen, date, startTime, endTime, readonly, onTimeChange, initialData, start, end]);

  useEffect(() => {
    if (projectId) {
      // get zuid from url
      const urlParams = new URLSearchParams(window.location.search);
      const zuid = urlParams.get('zuid');
      fetch(`${process.env.REACT_APP_API_BASE_URL}/get-tasks-by-project?projectId=${projectId}&zuid=${zuid}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setProjectTimecodes(data.data);
            // Only clear timecode if it's not valid for the new project (preserve user selection when possible)
            if (timecodeId && !data.data.find(tc => tc.id === timecodeId)) {
              setTimecodeId('');
            }
          } else {
            setProjectTimecodes([]);
            setTimecodeId(''); // Clear if no tasks found
          }
        })
        .catch(() => {
          setProjectTimecodes([]);
          setTimecodeId(''); // Clear on error
        });
    } else {
      // Clear timecodes when no project selected
      setProjectTimecodes([]);
      setTimecodeId('');
    }
  }, [projectId, timecodeId]);

  // Auto-set billing type when a timecode is selected
  useEffect(() => {
    const selected = projectTimecodes.find(tc => tc.id === timecodeId);
    if (selected) {
      setBillingType(selected.billingtype || '');
    } else {
      setBillingType('');
    }
  }, [timecodeId, projectTimecodes]);

  // Auto-fill notes based on worktype selection
  useEffect(() => {
    const selected = projectTimecodes.find(tc => tc.id === timecodeId);
    if (selected) {
      // Skip auto-fill if this is an existing entry with the same timecode (preserve Zoho Projects notes)
      if (initialData && initialData.timecodeId === timecodeId && initialData.title && initialData.title.trim()) {
        return; // Don't overwrite original notes from Zoho Projects
      }

      if (selected.billingtype === 'Non Billable') {
        // Set notes to worktype abbreviation for non-billable tasks
        const workTypeName = selected.name || '';
        const abbreviation = extractAbbreviation(workTypeName);
        const currentTitle = title;
        
        // Check if current title contains "|" separator
        if (currentTitle.includes(' | ')) {
          // Replace only the worktype part (before "|"), keep user text after "|"
          const userText = currentTitle.split(' | ').slice(1).join(' | ');
          setTitle(`${abbreviation} | ${userText}`);
        } else {
          // For Non-Billable, preserve user text and just add abbreviation prefix
          // Check if currentTitle is any worktype abbreviation pattern (starts with "(" and ends with ")")
          const isWorktypeAbbreviation = /^\([^)]+\)$/.test(currentTitle.trim());
          
          if (currentTitle.trim() && !isWorktypeAbbreviation) {
            setTitle(`${abbreviation} | ${currentTitle}`);
          } else {
            setTitle(abbreviation);
          }
        }
      } else {
        // For billable tasks, preserve user-entered text
        // Only clear if it's clearly auto-generated (contains specific patterns) AND empty of user content
        const isAutoGenerated = title.includes(' | ') && title.split(' | ').length === 2 && !title.split(' | ')[1].trim();
        
        if (isAutoGenerated || !title.trim()) {
          setTitle('');
        }
        // If user has written custom text, always preserve it
      }
    }
  }, [timecodeId, projectTimecodes, initialData]);

  // Check for vacation rounding when hours or timecode changes
  useEffect(() => {
    if (hours && timecodeId) {
      try {
        const currentHours = parseHHMMToDecimalHours(hours);
        const selectedTimecode = projectTimecodes.find(tc => tc.id === timecodeId);
        const timecodeName = selectedTimecode?.name || '';
        
        if (timecodeName.toUpperCase().includes('FER')) {
          const roundedHours = applyVacationRounding(currentHours, timecodeName);
          if (roundedHours !== currentHours) {
            const dayType = roundedHours === 3.75 ? 'half day' : 'full day';
            setVacationRoundingMessage(`Holiday hours will be rounded to ${roundedHours} hours (${dayType})`);
          } else {
            setVacationRoundingMessage('');
          }
        } else {
          setVacationRoundingMessage('');
        }
      } catch (e) {
        setVacationRoundingMessage('');
      }
    } else {
      setVacationRoundingMessage('');
    }
  }, [hours, timecodeId, projectTimecodes]);

  useEffect(() => {
    validateForm();
  }, [date, startTime, hours, projectId, timecodeId, title]);

  // Tilføj event listener for Escape-tasten
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, hasChanges, readonly]);

  // const roundUpToNearest6 = (minutes) => Math.ceil(minutes / 6) * 6;

  const validateForm = () => {
    const newErrors = {};

    if (!date) newErrors.date = "Please select a date";
    if (!startTime) newErrors.startTime = "Please enter a start time";
    if (!hours) newErrors.hours = "Please enter hours";
    else {
      // Support both HH:MM and decimal formats
      const hhmmPattern = /^\d{1,2}:\d{2}$/;
      const decimalPattern = /^\d*[,.]?\d+$/;
      
      if (!hhmmPattern.test(hours) && !decimalPattern.test(hours)) {
        newErrors.hours = "Use format like 1:30, 0,1 or 1.5";
      } else {
        try {
          const numericHours = parseFlexibleTimeToDecimalHours(hours);
          if (numericHours <= 0) newErrors.hours = "Hours must be greater than zero";
          if (numericHours > 23) newErrors.hours = "Hours must be less than 23";
        } catch (e) {
          newErrors.hours = "Invalid time format";
        }
      }
    }
    if (!projectId) newErrors.projectId = "Please select a matter";
    if (!timecodeId) newErrors.timecodeId = "Please select a worktype";
    if (!title.trim()) newErrors.title = "Please enter notes";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    setHasSubmitted(true);  // mark that user tried to submit
    if (!validateForm()) {
      return; // stop if validation fails
    }
    const startDate = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm').toDate();

    let floatHours;
    try {
      floatHours = parseFlexibleTimeToDecimalHours(hours);
    } catch (e) {
      console.error('Invalid hours entered:', hours);
      setErrors(prev => ({ ...prev, hours: 'Invalid time format' }));
      return;
    }

    if (isNaN(floatHours) || floatHours <= 0) {
      console.error('Invalid hours entered:', hours);
      setErrors(prev => ({ ...prev, hours: 'Invalid hours entered' }));
      return;
    }

    // Apply vacation rounding for FER timecodes
    const selectedTimecode = projectTimecodes.find(tc => tc.id === timecodeId);
    const timecodeName = selectedTimecode?.name || '';
    const originalHours = floatHours;
    floatHours = applyVacationRounding(floatHours, timecodeName);

    const durationMs = floatHours * 3600000; // Convert hours to ms
    const endDate = new Date(startDate.getTime() + durationMs);

    onSave({
      title,
      start: startDate,
      end: endDate,
      projectId,
      timecodeId,
      billingtype,
      logId: initialData?.logId || null,
      projectName: projectOptions.find(p => p.value === projectId)?.label || '',
      timecodeName: timecodeOptions.find(tc => tc.value === timecodeId)?.label || '',
      sendToCapLegal,
    });

    setHasSubmitted(false);
    resetForm();
    onClose();
  };


  const handleDelete = () => {
    setShowDeleteConfirmModal(true);
  };

  const handleConfirmDelete = () => {
    onDelete(initialData?.logId, initialData?.projectId, initialData?.timecodeId);
    resetForm();
    onClose();
    setShowDeleteConfirmModal(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmModal(false);
  };

  const handleClose = () => {
    if (hasChanges && !readonly) {
      setShowConfirmModal(true);
    } else {
      resetForm();
      onClose();
      setHasSubmitted(false);
      setHasChanges(false);
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmModal(false);
    resetForm();
    onClose();
    setHasSubmitted(false);
    setHasChanges(false);
  };

  const handleCancelClose = () => {
    setShowConfirmModal(false);
  };

  // Tjek for ændringer når inputs ændres
  useEffect(() => {
    if (!readonly) {
      setHasChanges(
        projectId !== (initialData?.projectId || '') ||
        timecodeId !== (initialData?.timecodeId || '') ||
        title !== (initialData?.title || '') ||
        sendToCapLegal !== true  // Tjek om sendToCapLegal er ændret fra default true
      );
    }
  }, [projectId, timecodeId, title, initialData, readonly, sendToCapLegal]);

  if (!isOpen) return null;

  //const day = moment(selectedSlotDate || start || new Date()).day(); // 0 (Sun) - 6 (Sat)

  //let positionStyle = { right: '20px' }; // default til højre
//
  //if (currentView === 'week') {
  //  if (day >= 4) { // Torsdag (4), Fredag (5), Lørdag (6)
  //    positionStyle = { left: '20px' };
  //  }
  //} else if (currentView === 'work_week') {
  //  if (day >= 3) { // Onsdag (3), Torsdag (4), Fredag (5)
  //    positionStyle = { left: '20px' };
  //  }
  //}

  // Tilføj fixed position og z-index
  //positionStyle = {
  //  ...positionStyle,
  //  position: 'fixed',
  //  top: '80px',
  //  zIndex: 1050,
  //};

  const projectOptions = projects.map((p) => ({
    value: p.id,
    label: p.name,
  }));

  const timecodeOptions = projectTimecodes.map((tc) => ({
    value: tc.id,
    label: tc.name,
  }));


  // Mobile-first positioning: center on mobile, use desktop positioning on larger screens
  const mobileStyles = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: 100,
  };

  const finalModalPosition = isMobile ? mobileStyles : modalPosition;

  return (
    <>
      {/* Backdrop overlay for all screen sizes */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 md:bg-transparent"
        style={{ zIndex: 99 }}
        onClick={handleClose}
      />
        
      <div
        className="bg-white p-4 md:p-6 rounded-lg w-full max-w-[95vw] md:w-[400px] max-h-[95vh] md:max-h-[90vh] overflow-y-auto shadow-xl border border-gray-300"
        style={finalModalPosition}
        onClick={(e) => e.stopPropagation()}
      >
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {initialData ? 'Edit' : 'New entry'}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 p-1 transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Approved Warning Badge */}
          {readonly && (
            <div className="mb-6 p-3 bg-[#d6c08b] border border-[#ccb778] rounded-md">
              <p className="text-white text-sm font-bold">
                If you wish to change this approved registration, please{' '}
                <a 
                  href={(() => {
                    const zuid = new URLSearchParams(window.location.search).get('zuid');
                    const formatDate = (dateStr) => {
                      const date = new Date(dateStr);
                      return date.toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit', 
                        year: 'numeric'
                      });
                    };
                    const subject = `Timelog ${initialData?.logId || ''} - ${initialData?.start ? formatDate(initialData.start) : ''}`;
                    const body = `Hello - regarding timelog ${initialData?.logId || ''} on ${initialData?.start ? formatDate(initialData.start) : ''} for ${initialData?.projectName || ''} with ${initialData?.timecodeName || ''} I have the following request:

XX

zuid: ${zuid || ''}`;
                    
                    return `mailto:finance@moalemweitemeyer.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                  })()}
                  className="underline hover:text-yellow-200"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  contact Finance
                </a>.
              </p>
            </div>
          )}

          {/* Date og Hours - responsive layout */}
          <div className="mb-4 flex flex-col md:flex-row gap-4">
            <div className="flex flex-col w-full md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Date:</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={`p-3 md:p-2 text-base rounded border border-gray-300 w-full touch-manipulation ${readonly ? 'bg-gray-50 text-gray-400' : ''}`}
                disabled={readonly}
              />
              {hasSubmitted && errors.date && <div className="text-red-500 text-sm mt-1">{errors.date}</div>}
            </div>
            <div className="flex flex-col w-full md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Hours:</label>
              <input
                type="text"
                value={hours}
                onChange={(e) => {
                  // Allow numbers, colon, comma, and period for flexible input
                  let val = e.target.value.replace(/[^0-9:,.]/g, '');
                  setHours(val);
                  setLastChangedField('hours');
                }}
                onBlur={() => {
                  // Try to parse and validate the hours input using flexible parser
                  try {
                    const parsed = parseFlexibleTimeToDecimalHours(hours);
                    if (!isNaN(parsed) && parsed > 0 && parsed <= 23) {
                      // Valid: update end time
                      const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
                      const endMoment = startMoment.clone().add(parsed, 'hours');
                      setEndTime(endMoment.format('HH:mm'));
                      // Notify parent
                      if (onTimeChange) {
                        onTimeChange({
                          ...(initialData || {}),
                          start: startMoment.toDate(),
                          end: endMoment.toDate(),
                          logId: initialData?.logId || null,
                        });
                      }
                    } else {
                      // Invalid: revert to current diff between start and end
                      const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
                      const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
                      if (startMoment.isValid() && endMoment.isValid() && endMoment.isAfter(startMoment)) {
                        const diffInHours = endMoment.diff(startMoment, 'minutes') / 60;
                        setHours(formatDecimalHoursToHHMM(diffInHours));
                      } else {
                        setHours('');
                      }
                    }
                  } catch (e) {
                    // Invalid format: revert to current diff between start and end
                    const startMoment = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
                    const endMoment = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
                    if (startMoment.isValid() && endMoment.isValid() && endMoment.isAfter(startMoment)) {
                      const diffInHours = endMoment.diff(startMoment, 'minutes') / 60;
                      setHours(formatDecimalHoursToHHMM(diffInHours));
                    } else {
                      setHours('');
                    }
                  }
                }}
                className={`p-3 md:p-2 text-base rounded border border-gray-300 w-full touch-manipulation ${readonly ? 'bg-gray-50 text-gray-400' : ''}`}
                disabled={readonly}
                placeholder="E.g. 1:30 or 0,1 or 1.5"
              />
              {hasSubmitted && errors.hours && <div className="text-red-500 text-sm mt-1">{errors.hours}</div>}
              {vacationRoundingMessage && (
                <div className="text-gray-500 text-xs mt-1 font-medium">
                  {vacationRoundingMessage}
                </div>
              )}
            </div>
          </div>

          {/* Start Time og End Time - responsive layout */}
          <div className="mb-4 flex flex-col md:flex-row gap-4">
            <div className="flex flex-col w-full md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Start Time:</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => {
                  setStartTime(e.target.value);
                  setLastChangedField('startTime');
                }}
                className={`p-3 md:p-2 text-base rounded border border-gray-300 w-full touch-manipulation ${readonly ? 'bg-gray-50 text-gray-400' : ''}`}
                disabled={readonly}
              />
              {hasSubmitted && errors.startTime && <div className="text-red-500 text-sm mt-1">{errors.startTime}</div>}
            </div>
            <div className="flex flex-col w-full md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">End Time:</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => {
                  setEndTime(e.target.value);
                  setLastChangedField('endTime');
                }}
                className={`p-3 md:p-2 text-base rounded border border-gray-300 w-full touch-manipulation ${readonly ? 'bg-gray-50 text-gray-400' : ''}`}
                disabled={readonly}
              />
              {hasSubmitted && errors.endTime && <div className="text-red-500 text-sm mt-1">{errors.endTime}</div>}
            </div>
          </div>

          {/* Matter som fuld bredde */}
          <div className="mb-4 flex flex-col">
            <label className="text-sm md:text-base font-medium mb-1">Matter:</label>
            <Select
              options={projectOptions}
              value={projectOptions.find(option => option.value === projectId)}
              onChange={(selectedOption) => {
                // Allow changing project if canEditProject is true
                if (!initialData || canEditProject) {
                  setProjectId(selectedOption?.value || '');
                }
              }}
              isClearable={!initialData || canEditProject}
              isDisabled={!!initialData && !canEditProject}
              placeholder="Search or select a matter..."
              styles={{
                control: (provided) => ({
                  ...provided,
                  minHeight: '44px', // Touch-friendly height on mobile
                  padding: '2px 4px',
                  fontSize: '16px', // Prevents zoom on iOS
                }),
                placeholder: (provided) => ({
                  ...provided,
                  fontSize: '16px',
                }),
                input: (provided) => ({
                  ...provided,
                  fontSize: '16px',
                }),
                singleValue: (provided) => ({
                  ...provided,
                  fontSize: '16px',
                }),
              }}
            />
            {hasSubmitted && errors.projectId && <div className="text-red-500 text-sm mt-1">{errors.projectId}</div>}
          </div>

          {/* Worktype som fuld bredde */}
          <div className="mb-4 flex flex-col">
            <label className="text-sm md:text-base font-medium mb-1">Worktype:</label>
            <Select
              options={timecodeOptions}
              value={timecodeOptions.find(option => option.value === timecodeId)}
              onChange={(selectedOption) => setTimecodeId(selectedOption?.value || '')}
              isClearable
              isDisabled={!projectId || readonly}
              placeholder={projectId ? "Search or select a worktype..." : "Select a matter first..."}
              styles={{
                control: (provided) => ({
                  ...provided,
                  minHeight: '44px', // Touch-friendly height on mobile
                  padding: '2px 4px',
                  fontSize: '16px', // Prevents zoom on iOS
                }),
                placeholder: (provided) => ({
                  ...provided,
                  fontSize: '16px',
                }),
                input: (provided) => ({
                  ...provided,
                  fontSize: '16px',
                }),
                singleValue: (provided) => ({
                  ...provided,
                  fontSize: '16px',
                }),
              }}
            />
            {hasSubmitted && errors.timecodeId && <div className="text-red-500 text-sm mt-1">{errors.timecodeId}</div>}
          </div>

          {billingtype && (
            <div className="mb-4 flex flex-col">
              <label className="text-sm md:text-base font-medium mb-1">Billing Type:</label>
              <input
                type="text"
                value={billingtype}
                disabled
                className="p-3 md:p-2 text-base rounded border border-gray-300 bg-gray-50 text-gray-400 w-full touch-manipulation"
              />
            </div>
          )}

          {/* Notes som fuld bredde */}
          <div className="mb-0 flex flex-col">
            <label className="text-sm md:text-base font-medium mb-1">Notes:</label>
            <textarea
              value={title}
              onChange={(e) => {
                const newValue = e.target.value.trimStart();
                const selected = projectTimecodes.find(tc => tc.id === timecodeId);
                
                // Check if this is a Non-Billable worktype and user is typing after worktype abbreviation
                if (selected && selected.billingtype === 'Non Billable' && selected.name) {
                  const workTypeName = selected.name;
                  const abbreviation = extractAbbreviation(workTypeName);
                  
                  // If current value is just the worktype abbreviation and user is adding text
                  if (title === abbreviation && newValue.length > abbreviation.length && !newValue.includes(' | ')) {
                    // Add " | " separator and the new text
                    const userText = newValue.substring(abbreviation.length);
                    setTitle(`${abbreviation} | ${userText}`);
                    return;
                  }
                }
                
                setTitle(newValue);
              }}
              className={`p-3 md:p-2 text-base rounded border border-gray-300 resize-none touch-manipulation ${readonly ? 'bg-gray-50 text-gray-400' : ''}`}
              placeholder="Enter notes..."
              disabled={readonly}
              maxLength={255}
              rows={4}
              style={{ fontSize: '16px' }} // Prevents zoom on iOS
            />
            <div className="text-sm text-gray-600 text-right">
              {title.length}/255
            </div>
            {hasSubmitted && errors.title && <div className="text-red-500 text-sm mt-1">{errors.title}</div>}
          </div>

          {/* Send to CapLegal with delay checkbox */}
          <div className="mb-6 flex items-center">
            <input
              type="checkbox"
              id="sendToCapLegal"
              className="mr-3 h-5 w-5 md:h-4 md:w-4 accent-[#537D5D] cursor-pointer touch-manipulation"
              disabled={readonly}
              checked={sendToCapLegal}
              onChange={(e) => setSendToCapLegal(e.target.checked)}
            />
            <label htmlFor="sendToCapLegal" className="cursor-pointer text-sm md:text-base">Send to CapLegal with delay</label>
          </div>

          <div className="flex flex-col md:flex-row justify-between gap-3 md:gap-0">
            {!readonly && (
              <>
                <div className="order-2 md:order-1">
                  {initialData && (
                    <button
                      onClick={handleDelete}
                      className="w-full md:w-auto px-6 py-3 md:px-4 md:py-2 bg-red-600 text-white rounded hover:bg-red-700 touch-manipulation font-medium"
                    >
                      Delete
                    </button>
                  )}
                </div>
                <div className="order-1 md:order-2">
                  <button
                    onClick={handleSubmit}
                    className="w-full md:w-auto px-6 py-3 md:px-4 md:py-2 bg-[#537D5D] text-white rounded hover:bg-[#73946B] disabled:opacity-50 touch-manipulation font-medium"
                    disabled={readonly}
                  >
                    Save
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      
      <ConfirmModal 
        isOpen={showConfirmModal}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
        title="Unsaved Changes"
        message="Are you sure you want to discard your changes?"
        confirmText="Discard"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
      <ConfirmModal 
        isOpen={showDeleteConfirmModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Delete Registration"
        message="Are you sure you want to delete this time registration?"
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </>
  );
}